import { toast } from "@/ui-plus/sonner";
import { computed } from "vue";
import { authState } from "@/ui-plus/useAuth";
import { deliveryteamOrdersApiClient } from "@api-client/deliveryteam_orders";
import { ref } from "vue";
import type { ResType } from "@api-client/typeUtils";
import { myDeliveryteams } from "@deliveryteam-spa-orders-vue/refs/MyDeliveryteams";
import { NOT_AUTHENTICATED } from "@lib/consts/authConsts";

const ApiPath = deliveryteamOrdersApiClient[":deliveryteamId"].get_by_order_ids.$post;
type RecentOrdersType = ResType<typeof ApiPath>["data"];
export type ordersType = RecentOrdersType[number];

const recentOrdersIds = ref<{
    order_placed_at_ts: number;
    deliveryteamOrderId: string;
}[]>([]);
export const cachedDeliveryteamOrders = ref<Record<string, ordersType>>({});

export let autoLoadTimer: Record<string, ReturnType<typeof setTimeout> | null> = {}
export const stopAutoRefreshRecentOrderIds = (deliveryteamId: string) => {
    if (autoLoadTimer[deliveryteamId]) {
        clearTimeout(autoLoadTimer[deliveryteamId]);
    }
}

export const initCourierActiveOrders = async () => {
    const { data, error } = await (await deliveryteamOrdersApiClient.get_courier_active_orders.$post({
        json: {
            courierId: authState.value?.accessTokenPayload?.id,
        }
    })).json();
    if (error) {
        toast.error(error.message ?? "Error fetching recent orders");
    }
    if (data) {
        for (let i = 0; i < data.length; i++) {
            // console.log('loading order', data[i]);
            await loadOrders(data[i].deliveryteamId, [data[i].deliveryteamOrderId]);
        }
    }
}

export const refreshRecentOrderIds = async (deliveryteamId: string) => {
    stopAutoRefreshRecentOrderIds(deliveryteamId);

    if (!myDeliveryteams.value.some(deliveryteam => deliveryteam.id === deliveryteamId && deliveryteam.isUserActivatedTeam)) return;

    const { data, error } = await (await deliveryteamOrdersApiClient[":deliveryteamId"].recent_orders.$post({
        param: { deliveryteamId },
        json: {
            start: undefined,
            end: undefined,
        }
    })).json();
    if (error) {
        toast.error(error.message ?? "Error fetching recent orders");
        setTimeout(() => {
            if (error.code === NOT_AUTHENTICATED) {
                window.location.reload();
            }
        }, 2000);
    } else {
        const newOrderIds: string[] = [];
        recentOrdersIds.value = data;
        data.forEach((order) => {
            // filter new and updated orders from cached orders
            const cachedOrder = cachedDeliveryteamOrders.value[order.deliveryteamOrderId];
            if (!cachedOrder || cachedOrder.updated_at_ts !== order.updated_at_ts) {
                newOrderIds.push(order.deliveryteamOrderId);
            }
        });
        // load new and updated orders
        if (newOrderIds.length > 0) {
            loadOrders(deliveryteamId, newOrderIds);
        }
    }

    autoLoadTimer[deliveryteamId] = setTimeout(() => {
        refreshRecentOrderIds(deliveryteamId);
    }, 5 * 60_000); // 5 minutes
}

export const loadOrders = async (deliveryteamId: string, newOrderIds: string[]) => {
    const { data, error } = await (await deliveryteamOrdersApiClient[":deliveryteamId"].get_by_order_ids.$post({
        param: { deliveryteamId },
        json: {
            orderIds: newOrderIds,
            // columns: {
            //     deliveryteamOrderId: true,
            //     orderType: true,
            //     orderStatus: true,
            //     orderTotal: true,
            //     orderNote: true,
            //     orderCreatedAtTs: true,
            //     orderUpdatedAtTs: true,
            //     orderCreatedAt: true,
            //     orderUpdatedAt: true,
            //     order_placed_at_ts: true,
            // }
        },

    })).json();
    if (error) {
        toast.error(error.message ?? "Error fetching recent orders");
        return;
    } else {
        data.forEach((order) => {
            order.statusHistory.sort((a, b) => a.created_at_ts - b.created_at_ts);
            cachedDeliveryteamOrders.value[order.deliveryteamOrderId] = order;
        });
    }
}



export const isMyOrdersOnly = ref(true);

export const myOrdersList = computed(() => {
    return Object.values(cachedDeliveryteamOrders.value)
        .filter(order => order.courierId === authState.value?.accessTokenPayload?.id)
        .sort((a, b) => b.order_placed_at_ts - a.order_placed_at_ts);
});

export const ordersList = computed(() => {
    return isMyOrdersOnly.value ?
        myOrdersList.value :
        (
            Object.values(cachedDeliveryteamOrders.value)
                .sort((a, b) => b.order_placed_at_ts - a.order_placed_at_ts)
        );
});